"use client";

import { useState } from "react";

type Props = {
    /** Absolute URL of the article — used as the share target */
    url: string;
    /** Article title — pre-fills X / LinkedIn share intents */
    title: string;
};

/**
 * Outrank-style share row for blog posts. Renders 4 round icon
 * buttons: copy link, X (Twitter), Facebook, LinkedIn. The copy
 * button flips to a green check for 2 seconds on success so the user
 * gets visual feedback without needing a toast system.
 *
 * No third-party SDKs — every share button is a plain <a> with the
 * provider's documented intent URL. Zero JS cost beyond the copy
 * handler.
 */
export default function BlogShareButtons({ url, title }: Props) {
    const [copied, setCopied] = useState(false);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback for older browsers / insecure contexts where
            // navigator.clipboard isn't available. Selects the URL
            // into a hidden textarea + execCommand('copy').
            const ta = document.createElement("textarea");
            ta.value = url;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch {
                /* swallow — user can copy from URL bar */
            }
            document.body.removeChild(ta);
        }
    }

    const enc = encodeURIComponent;
    const xUrl = `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;

    return (
        <div className="blog-share-row" role="group" aria-label="Share this article">
            <button
                type="button"
                onClick={handleCopy}
                className={`blog-share-btn${copied ? " is-copied" : ""}`}
                aria-label={copied ? "Link copied to clipboard" : "Copy link"}
                title={copied ? "Copied!" : "Copy link"}
            >
                {copied ? (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 7 12 13 4" />
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.5 4.5l1.5-1.5a3 3 0 0 1 4.243 4.243L13 9.5" />
                        <path d="M6.5 11.5l-1.5 1.5a3 3 0 0 1-4.243-4.243L3 6.5" />
                        <line x1="6" y1="10" x2="10" y2="6" />
                    </svg>
                )}
            </button>

            <a
                href={xUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn"
                aria-label="Share on X (Twitter)"
                title="Share on X"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            </a>

            <a
                href={fbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn"
                aria-label="Share on Facebook"
                title="Share on Facebook"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
                </svg>
            </a>

            <a
                href={liUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-share-btn"
                aria-label="Share on LinkedIn"
                title="Share on LinkedIn"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.339 18.337V9.755H5.667v8.582h2.672zM7.003 8.575a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zM18.336 18.337v-4.701c0-2.31-.5-4.085-3.196-4.085-1.296 0-2.166.71-2.522 1.385h-.037V9.755h-2.567v8.582h2.671v-4.246c0-1.12.213-2.205 1.601-2.205 1.37 0 1.388 1.282 1.388 2.277v4.174h2.662z" />
                </svg>
            </a>
        </div>
    );
}
